/* app/editor.jsx - two-pane content editor + deep SEO panel + article generator.
   Wired to the live backend: GET/POST/PUT /api/wp/posts(/:id) + /api/wp/seo-meta/:id,
   scoped to the active site. Markup is the original design. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
import { capSecondary, secondaryAdvice, MAX_SECONDARY } from '../lib/keyword-rule.js';
import { plainTitle } from '../lib/wp-text.js';
(function () {
  const { useState, useEffect, useRef } = React;
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }
  const DS = () => window.HuddleDesignSystem_775113;

  const siteUrlOf = (site) => {
    const u = site && (site.urlFull || site.url);
    if (!u) return null;
    return u.startsWith("http") ? u : "https://" + u;
  };
  // seo_meta secondary/lsi → chip array
  function parseSecondary(meta) {
    if (!meta) return [];
    if (meta.lsi_keywords) { try { const a = JSON.parse(meta.lsi_keywords); if (Array.isArray(a)) return a; } catch { /* not json */ } }
    if (meta.secondary_keywords) return String(meta.secondary_keywords).split(",").map(s => s.trim()).filter(Boolean);
    return [];
  }

  function SeoGauge({ score, size = 84 }) {
    const r = (size - 12) / 2, c = 2 * Math.PI * r;
    const tone = score >= 75 ? "success" : score >= 50 ? "warning" : "danger";
    const color = `var(--${tone})`;
    return (
      <div className="ml-gauge">
        <div className="ml-gauge__ring" style={{ width: size, height: size }}>
          <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--gray-200)" strokeWidth="7" />
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - score/100)} style={{ transition: "stroke-dashoffset .6s cubic-bezier(.16,.84,.44,1)" }} />
          </svg>
          <div className="ml-gauge__num"><span className="ml-gauge__val" style={{ fontSize: "var(--text-lg)" }}>{score}</span></div>
        </div>
      </div>
    );
  }

  function counter(len, lo, hi) {
    if (len === 0) return { cls: "", text: `0 / ${lo}-${hi}` };
    if (len < lo) return { cls: "ml-counter--warn", text: `${len} · add ${lo - len}` };
    if (len > hi) return { cls: "ml-counter--bad", text: `${len} · ${len - hi} over` };
    return { cls: "ml-counter--ok", text: `${len} · good` };
  }

  // ---- The editor ----
  // `planLink` is the plan piece this draft was generated for
  // ({ plan_id, item_id, site_id, title }). A piece only becomes a real post
  // here, so this is the one place that can record which post it became.
  function Editor({ post, gen, planLink, inboxLink, pageLink, onBack, site, type = "post" }) {
    // Pages load and save through /wp/pages; mixing them into /wp/posts
    // 404s on load and would create a page's edits as a post.
    const wpBase = type === "page" ? "pages" : "posts";
    const { Button, IconButton, Field, Input, Textarea, Badge, Select, Switch } = DS();
    const seed = post || {};
    const g = gen || null;   // a real, AI-generated article handed off from the generator
    const toArr = (v) => Array.isArray(v) ? v.filter(Boolean) : (typeof v === "string" ? v.split(",").map(s => s.trim()).filter(Boolean) : []);
    const siteUrl = siteUrlOf(site);
    const bodyRef = useRef(null);

    const [postId, setPostId] = useState(seed.id || null);
    const [title, setTitle] = useState(seed.title || (g && g.title) || "");
    const [status, setStatus] = useState(seed.status === "published" ? "published" : "draft");
    const [focusKw, setFocusKw] = useState((g && g.focusKeyword) || "");
    // A draft handed over by the generator is already capped by the server; it
    // is capped again here because neither side should be the only guard, and
    // because this state is what the Add button counts against.
    const [secondary, setSecondary] = useState(
      g ? capSecondary(toArr(g.secondaryKeywords), (g && g.focusKeyword) || "").secondary : []);
    // The chip being typed. null is "the Add button is showing"; a string, even
    // an empty one, is "the input is open".
    const [kwDraft, setKwDraft] = useState(null);
    const [slug, setSlug] = useState(String(seed.slug || (g && g.slug) || "").replace(/^\//, ""));
    const [titleTag, setTitleTag] = useState((g && g.titleTag) || "");
    const [metaDesc, setMetaDesc] = useState((g && g.metaDescription) || "");
    const [ogTitle, setOgTitle] = useState((g && g.ogTitle) || "");
    const [ogDesc, setOgDesc] = useState((g && g.ogDescription) || "");
    const [canonical, setCanonical] = useState("");
    const [noindex, setNoindex] = useState(false);
    const [nofollow, setNofollow] = useState(false);
    const [schemaType, setSchemaType] = useState((g && g.schemaType) || "");
    const [schemaJson, setSchemaJson] = useState("");
    const [schemaBusy, setSchemaBusy] = useState(false);
    const [score, setScore] = useState(seed.seo || 0);
    // "siteatlas" when saving title tag + meta description also publishes them
    // to the live page head (SiteAtlas override keys); null otherwise.
    const [writeback, setWriteback] = useState(null);
    const [filling, setFilling] = useState(false);
    const [applied, setApplied] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [jsonOpen, setJsonOpen] = useState(false);
    const [saved, setSaved] = useState("");
    const [loading, setLoading] = useState(!!(seed.id && siteUrl));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);   // body never arrived → editing is locked
    const [emptyBody, setEmptyBody] = useState(false);  // body arrived and is genuinely empty in WP
    const [reloadTick, setReloadTick] = useState(0);
    // The write-back to the plan piece: null = not attempted, {ok} = it landed,
    // {ok:false, why} = the post saved but the piece did not learn about it.
    // Kept apart from `error` because those are different outcomes: one means
    // nothing was saved, the other means the article is safe and only the
    // bookkeeping failed.
    const [linkState, setLinkState] = useState(null);

    // Load existing post content + SEO meta (or seed an empty/sample body).
    useEffect(() => {
      let cancel = false;
      if (!seed.id || !siteUrl) {
        if (bodyRef.current) bodyRef.current.innerHTML = (g && g.content) || "<p><br/></p>";
        return;
      }
      setLoading(true); setError(null); setLoadError(null); setEmptyBody(false);
      const enc = encodeURIComponent(siteUrl);
      Promise.all([
        api(`/wp/${wpBase}/${seed.id}?siteUrl=${enc}`).then(p => ({ p }), e => ({ e })),
        api(`/wp/seo-meta/${seed.id}?siteUrl=${enc}`).catch(() => null),
      ]).then(([post, metaRes]) => {
        if (cancel) return;
        if (post.e || !post.p) {
          // The body never arrived. Say so and lock the editor: a blank canvas
          // here is indistinguishable from an empty post, and saving one would
          // push the placeholder over the real content in WordPress.
          setLoadError((post.e && post.e.message) || "WordPress returned no data for this post.");
          if (bodyRef.current) bodyRef.current.innerHTML = "";
        } else {
          const p = post.p;
          // Decoded before it reaches the input. This field is SAVED back as
          // text, so loading `&#8217;` here would write the entity into the
          // post's title in WordPress, and no number of re-saves could undo it.
          if (p.title && p.title.rendered != null) setTitle(plainTitle(p.title));
          setStatus(p.status === "publish" ? "published" : "draft");
          if (p.slug) setSlug(String(p.slug).replace(/^\//, ""));
          const html = (p.content && p.content.rendered) || "";
          if (bodyRef.current) bodyRef.current.innerHTML = html || "<p><br/></p>";
          // `<p></p>` and friends: WordPress served the post, it just has no body.
          setEmptyBody(!html.replace(/<[^>]*>/g, "").replace(/&nbsp;|\s/g, ""));
        }
        setWriteback((metaRes && metaRes.writeback) || null);
        const m = metaRes && metaRes.meta;
        if (m) {
          setFocusKw(m.focus_keyword || "");
          setTitleTag(m.title_tag || "");
          setMetaDesc(m.meta_description || "");
          setOgTitle(m.og_title || "");
          setOgDesc(m.og_description || "");
          setCanonical(m.canonical || "");
          setNoindex(m.noindex === "1" || m.noindex === 1);
          setNofollow(m.nofollow === "1" || m.nofollow === 1);
          setSchemaType(m.schema_type || "");
          setSchemaJson(m.schema_json || "");
          if (m.slug) setSlug(String(m.slug).replace(/^\//, ""));
          // NOT capped. A post saved before the rule existed can hold six, and
          // silently showing three would be this screen lying about what is
          // stored. The counter reads "6 / 3" in red and the note says to cut
          // it back, which leaves the choice of which three with the author.
          setSecondary(parseSecondary(m));
          if (m.seo_score != null) setScore(Number(m.seo_score) || 0);
        }
        setLoading(false); setSaved(post.e || !post.p ? "" : "loaded");
      });
      return () => { cancel = true; };
    }, [seed.id, siteUrl, reloadTick]); // eslint-disable-line react-hooks/exhaustive-deps

    // What is SAVED is what is on screen, including a legacy list still over the
    // limit. Trimming it here would delete keywords the author never chose to
    // drop, on a save they made for an unrelated reason: the note tells them to
    // cut it back, and cutting it back stays their edit.
    const saveMeta = (id) => api(`/wp/seo-meta/${id}`, { method: "POST", body: JSON.stringify({
      siteUrl, post_type: type,
      focus_keyword: focusKw,
      secondary_keywords: secondary.join(", "),
      lsi_keywords: JSON.stringify(secondary),
      slug, title_tag: titleTag, meta_description: metaDesc,
      og_title: ogTitle, og_description: ogDesc,
      canonical, noindex: noindex ? "1" : "0", nofollow: nofollow ? "1" : "0",
      schema_type: schemaType, schema_json: schemaJson,
      seo_score: score,
    }) });

    // Tell the plan piece which post it became. Only callable once a save has
    // returned a real post id, which is why nothing upstream could do it: the
    // generator has the piece but no post, and WordPress has the post but no
    // idea a plan exists. Without this a generated piece stays at "idea"
    // forever with no draft attached and "Open draft" never appears.
    const linkPiece = async (id, published) => {
      if (!planLink || !planLink.plan_id || !planLink.item_id || !id) return;
      const sid = planLink.site_id != null ? planLink.site_id : (site && (site.site_id != null ? site.site_id : site.id));
      if (sid == null) { setLinkState({ ok: false, why: "no active site to record it against" }); return; }
      try {
        await api(`/content-plans/${planLink.plan_id}/items/${planLink.item_id}/link`, { method: "POST", body: JSON.stringify({
          // Status only ever moves forward server-side, so a piece already in
          // review is not dragged back by a second save.
          site_id: sid, post_id: id, status: published ? "published" : "drafting",
        }) });
        setLinkState({ ok: true, postId: id });
      } catch (e) {
        setLinkState({ ok: false, why: (e && e.message) || "the request failed" });
      }
    };

    const persist = async (doPublish) => {
      if (!siteUrl) { setError("No active site - connect a site first."); return; }
      // Never write an editor body we failed to fill, that would replace the
      // live article with an empty paragraph.
      if (loadError) { setError("This post's content didn't load, so saving is blocked - it would overwrite the article in WordPress with an empty body. Retry the load first."); return; }
      setSaving(true); setError(null);
      const content = bodyRef.current ? bodyRef.current.innerHTML : "";
      try {
        let id = postId;
        if (id) {
          await api(`/wp/${wpBase}/${id}`, { method: "PUT", body: JSON.stringify({ siteUrl, title, content, status: doPublish ? "publish" : "draft" }) });
        } else {
          const created = await api(`/wp/${wpBase}`, { method: "POST", body: JSON.stringify({ siteUrl, title, content, status: "draft" }) });
          id = created && created.id; setPostId(id);
          if (doPublish && id) await api(`/wp/${wpBase}/${id}`, { method: "PUT", body: JSON.stringify({ siteUrl, status: "publish" }) });
        }
        if (id) await saveMeta(id);
        setStatus(doPublish ? "published" : "draft");
        setSaved("just now");
        // After the post exists, and never on a failure: a piece must not claim
        // a draft that was not written.
        await linkPiece(id, doPublish);
        // Same contract for an inbox item that seeded this article: once a
        // real post exists, mark the item used and point it at the post, so
        // the inbox stops proposing work that is already done. Best-effort:
        // the article is saved either way.
        if (id && inboxLink && inboxLink.id) {
          api(`/inbox/${inboxLink.id}`, { method: "PATCH", body: JSON.stringify({ status: "used", linked_post_id: id }) }).catch(() => {});
        }
        // A bank page that seeded this article: "drafted" becomes a fact about
        // a real post instead of a hand-ticked box, and the bank's coverage
        // pipeline rule can now find the page by its linked post.
        if (id && pageLink && pageLink.pageId) {
          api(`/research/pages/${pageLink.pageId}`, { method: "PATCH", body: JSON.stringify({ status: "drafted", linked_post_id: id }) }).catch(() => {});
        }
      } catch (e) { setError(e.message || "Save failed"); }
      finally { setSaving(false); }
    };
    const saveDraft = () => persist(false);
    const doPublish = () => persist(true);

    const aiFill = async () => {
      if (!title && !focusKw) { setError("Add a title or focus keyword first."); return; }
      setFilling(true); setApplied(false); setError(null);
      try {
        const content = bodyRef.current ? (bodyRef.current.innerText || "").slice(0, 2000) : "";
        const r = await api(`/claude/seo`, { method: "POST", body: JSON.stringify({
          title, content, focusKeyword: focusKw,
          site_id: site && (site.site_id != null ? site.site_id : site.id),
        }) });
        const seo = (r && r.seo) || {};
        if (seo.titleTag) setTitleTag(seo.titleTag);
        if (seo.metaDescription) setMetaDesc(seo.metaDescription);
        if (seo.ogTitle) setOgTitle(seo.ogTitle);
        if (seo.ogDescription) setOgDesc(seo.ogDescription);
        if (seo.focusKeyword && !focusKw) setFocusKw(seo.focusKeyword);
        if (secondary.length === 0 && seo.secondaryKeywords) {
          setSecondary(capSecondary(toArr(seo.secondaryKeywords), seo.focusKeyword || focusKw).secondary);
        }
        if (seo.slug && !slug) setSlug(String(seo.slug).replace(/^\//, ""));
        setApplied(true);
      } catch (e) { setError(e.message || "AI Fill failed"); }
      finally { setFilling(false); }
    };

    // Real on-page content score (0-100) from Claude, drives the SEO gauge.
    const analyze = async () => {
      const content = bodyRef.current ? (bodyRef.current.innerText || "") : "";
      if (!title && !content.trim()) { setError("Add a title or some content first."); return; }
      setAnalyzing(true); setError(null);
      try {
        const r = await api(`/claude/content-gap`, { method: "POST", body: JSON.stringify({ title, content, focusKeyword: focusKw, metaDescription: metaDesc }) });
        if (r && typeof r.contentScore === "number") setScore(Math.round(r.contentScore));
        setAnalysis(r || null);
      } catch (e) { setError(e.message || "Analysis failed"); }
      finally { setAnalyzing(false); }
    };

    // Generate JSON-LD schema for the chosen type via Claude.
    const genSchema = async () => {
      if (!schemaType) return;
      setSchemaBusy(true); setError(null);
      try {
        const content = bodyRef.current ? (bodyRef.current.innerText || "").slice(0, 1500) : "";
        const postUrl = (post && post.link) || (siteUrl && slug ? siteUrl.replace(/\/$/, "") + "/" + slug : "");
        const r = await api(`/claude/generate-schema`, { method: "POST", body: JSON.stringify({ title, content, schemaType, focusKeyword: focusKw, siteUrl, postUrl, author: (site && site.name) || "" }) });
        if (r && r.schema) setSchemaJson(JSON.stringify(r.schema, null, 2));
      } catch (e) { setError(e.message || "Schema generation failed"); }
      finally { setSchemaBusy(false); }
    };

    const tt = counter(titleTag.length, 50, 60);
    const md = counter(metaDesc.length, 150, 160);

    // The keyword rule, as the panel states it: one primary, up to three
    // secondaries. Under target is a nudge (warn), over is a fault (bad).
    const kwAdvice  = secondaryAdvice(secondary.length);
    const kwAtLimit = secondary.length >= MAX_SECONDARY;
    const kwCls = kwAdvice.state === "on_target" ? "ml-counter--ok"
                : kwAdvice.state === "over"      ? "ml-counter--bad"
                                                 : "ml-counter--warn";

    // Commit whatever is in the new-chip input. capSecondary does the work the
    // old "add a chip literally reading 'new keyword'" button never did: it
    // trims, refuses a duplicate, refuses the focus keyword a second time, and
    // holds the ceiling even if a blur lands while the list is already full.
    const commitKwDraft = () => {
      const v = String(kwDraft === null ? "" : kwDraft).trim();
      setKwDraft(null);
      if (!v) return;
      setSecondary(s => capSecondary([...s, v], focusKw).secondary);
    };
    const flashCls = applied ? " ml-aiflash" : "";

    const statusBadge = status === "published"
      ? <Badge tone="success" dot>Published</Badge>
      : <span className="hds-badge hds-badge--draft"><span className="hds-badge__dot" />Draft</span>;

    return (
      <div>
        <div className="ml-editor-bar">
          <Button variant="ghost" size="sm" leadingIcon="chevron-left" onClick={onBack}>Posts</Button>
          {statusBadge}
          <span className="ml-editor-meta">· {saving ? "Saving…" : saved ? "Saved " + saved : "Unsaved"}</span>
          <span className="ml-editor-bar__spacer" />
          <span className="ml-editor-meta">{title ? title.split(/\s+/).length : 0} words</span>
          <Button variant="secondary" size="md" leadingIcon="check" disabled={saving || loading || !!loadError} onClick={saveDraft}>Save draft</Button>
          <Button variant="primary" size="md" leadingIcon="check-circle" disabled={saving || loading || !!loadError} onClick={doPublish}>Approve &amp; publish</Button>
        </div>
        <div style={{ marginBottom: 16 }}>
          {React.createElement(DS().Banner, { tone: "info", title: "Draft-first" }, "Every save lands in Draft. “Approve & publish” is the only action that makes this post live.")}
        </div>
        {/* The plan piece this draft belongs to. Three states, kept distinct:
            not saved yet, attached, and saved-but-not-attached. The last one is
            a warning rather than an error because the article is safe. */}
        {planLink && !linkState && (
          <div style={{ marginBottom: 16 }}>
            {React.createElement(DS().Banner, { tone: "info", title: "Part of a content plan" },
              `This draft is the piece “${planLink.title || "untitled"}”. Saving it attaches the post to that piece, which is what moves the plan along.`)}
          </div>
        )}
        {linkState && linkState.ok && (
          <div style={{ marginBottom: 16 }}>
            {React.createElement(DS().Banner, { tone: "info", title: "Attached to the plan piece" },
              `Post #${linkState.postId} is now the draft for “${(planLink && planLink.title) || "this piece"}”, and the plan counts it.`)}
          </div>
        )}
        {linkState && !linkState.ok && (
          <div style={{ marginBottom: 16 }}>
            {React.createElement(DS().Banner, { tone: "warning", title: "Saved, but the plan piece was not updated" },
              React.createElement("span", null,
                `The post is safe in WordPress. Recording it against the plan piece failed: ${linkState.why}. Until that lands, the plan still shows this piece with no draft. `,
                React.createElement("button", {
                  className: "ml-linkbtn",
                  onClick: () => linkPiece(postId, status === "published"),
                }, "Retry")))}
          </div>
        )}
        {loadError && (
          <div style={{ marginBottom: 16 }}>
            {React.createElement(DS().Banner, { tone: "danger", title: "Couldn't load this post's content" },
              React.createElement("span", null,
                loadError + " The body below is empty because nothing arrived from WordPress, not because the post is empty. Saving is disabled so the live article can't be overwritten. ",
                React.createElement("button", {
                  className: "ml-linkbtn",
                  onClick: () => setReloadTick(t => t + 1),
                }, "Retry")))}
          </div>
        )}
        {!loadError && emptyBody && (
          <div style={{ marginBottom: 16 }}>
            {React.createElement(DS().Banner, { tone: "warning", title: "This post has no content in WordPress" },
              "WordPress served the post, but its body is empty. Anything you write here will be the first content it has.")}
          </div>
        )}
        {error && (
          <div style={{ marginBottom: 16 }}>
            {React.createElement(DS().Banner, { tone: "danger", title: "Couldn't save" }, error)}
          </div>
        )}

        <div className="ml-editor">
          {/* main editor */}
          <div className="ml-edcard">
            <input className="ml-edtitle" value={title} onChange={e => setTitle(e.target.value)} placeholder="Add a title" />
            <div className="ml-edslug"><MlIcon name="link" size={14} /> {site ? site.url : "your-site.com"}<code>/{slug}</code></div>
            <div className="ml-toolbar">
              <button className="ml-toolbar__btn" title="Heading">H</button>
              <button className="ml-toolbar__btn" title="Bold" style={{ fontWeight: 800 }}>B</button>
              <button className="ml-toolbar__btn" title="Italic" style={{ fontStyle: "italic" }}>I</button>
              <span className="ml-toolbar__sep" />
              <button className="ml-toolbar__btn" title="Link"><MlIcon name="link" size={16} /></button>
              <button className="ml-toolbar__btn" title="List"><MlIcon name="list" size={16} /></button>
              <button className="ml-toolbar__btn" title="Image"><MlIcon name="file" size={16} /></button>
              <span className="ml-toolbar__sep" />
              <button className="ml-toolbar__btn" title="Code"><MlIcon name="code" size={16} /></button>
              <span style={{ flex: 1 }} />
              <span className="ml-editor-meta">HTML body</span>
            </div>
            <div ref={bodyRef} className="ml-body ml-prose" contentEditable={!loadError} suppressContentEditableWarning />
            {loading && <div className="ml-editor-meta" style={{ padding: "8px 2px" }}>Loading content…</div>}
            {!loading && loadError && <div className="ml-editor-meta" style={{ padding: "8px 2px" }}>Editing locked, content unavailable.</div>}
          </div>

          {/* SEO panel */}
          <div className="ml-seopanel">
            <div className="ml-seohead">
              <span className="ml-seohead__title"><MlIcon name="gauge" size={18} style={{ color: "var(--primary)" }} />SEO</span>
              {applied && <span className="ml-seo-applied"><MlIcon name="sparkles" size={14} />AI applied</span>}
            </div>
            <div className="ml-seo-fillbar">
              <Button variant="ai" fullWidth disabled={filling}
                      leadingIcon={filling ? <span className="ml-spinner ml-spinner--sm ml-spinner--ai" /> : <MlIcon name="sparkles" size={18} />}
                      onClick={aiFill}>{filling ? "Filling SEO…" : "AI Fill SEO"}</Button>
              {filling && <div className="ml-progress ml-progress--indeterminate" style={{ marginTop: 10 }}><div className="ml-progress__bar ml-progress__bar--ai" /></div>}
            </div>
            <div className="ml-seo-scroll">
              <div className="ml-seosection">
                <div className="ml-seo-gaugewrap">
                  <SeoGauge score={score} />
                  <ul className="ml-tips" style={{ flex: 1 }}>
                    <li className={focusKw ? "is-ok" : "is-bad"}><MlIcon name={focusKw ? "check-circle" : "x-circle"} size={16} />Focus keyword set</li>
                    <li className={tt.cls === "ml-counter--ok" ? "is-ok" : "is-warn"}><MlIcon name={tt.cls === "ml-counter--ok" ? "check-circle" : "alert-triangle"} size={16} />Title tag length</li>
                    <li className={md.cls === "ml-counter--ok" ? "is-ok" : "is-warn"}><MlIcon name={md.cls === "ml-counter--ok" ? "check-circle" : "alert-triangle"} size={16} />Meta description</li>
                    <li className={kwAdvice.state === "on_target" ? "is-ok" : kwAdvice.state === "over" ? "is-bad" : "is-warn"}
                        title={kwAdvice.note}>
                      <MlIcon name={kwAdvice.state === "on_target" ? "check-circle" : kwAdvice.state === "over" ? "x-circle" : "alert-triangle"} size={16} />
                      Keyword count (1 + {MAX_SECONDARY})
                    </li>
                  </ul>
                </div>
                <Button variant="secondary" size="sm" fullWidth disabled={analyzing}
                        leadingIcon={analyzing ? <span className="ml-spinner ml-spinner--sm" /> : <MlIcon name="gauge" size={16} />}
                        onClick={analyze} style={{ marginTop: 10 }}>{analyzing ? "Analyzing…" : score ? "Re-analyze content" : "Analyze content"}</Button>
                {analysis && (
                  <div style={{ marginTop: 10, fontSize: "var(--text-xs)", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                    {analysis.overallAssessment && <p style={{ margin: "0 0 8px" }}>{analysis.overallAssessment}</p>}
                    {Array.isArray(analysis.issues) && analysis.issues.slice(0, 4).map((it, i) => (
                      <div key={i} style={{ display: "flex", gap: 6, marginBottom: 5 }}>
                        <MlIcon name={it.severity === "high" ? "alert-circle" : it.severity === "medium" ? "alert-triangle" : "info"} size={14} style={{ flexShrink: 0, marginTop: 1, color: it.severity === "high" ? "var(--danger)" : it.severity === "medium" ? "var(--warning)" : "var(--text-muted)" }} />
                        <span><b>{it.description}</b>{it.suggestion ? " - " + it.suggestion : ""}</span>
                      </div>
                    ))}
                    {Array.isArray(analysis.missingTopics) && analysis.missingTopics.length > 0 && (
                      <p style={{ margin: "6px 0 0" }}><b>Missing:</b> {analysis.missingTopics.slice(0, 5).join(", ")}</p>
                    )}
                  </div>
                )}
              </div>

              <div className={"ml-seosection" + flashCls}>
                <Field label="Focus keyword" className=""><Input value={focusKw} onChange={e => setFocusKw(e.target.value)} placeholder="primary term to rank for" leadingIcon="search" /></Field>
              </div>

              <div className={"ml-seosection" + flashCls}>
                <span className="ml-seosection__label">
                  Secondary keywords
                  <span className={"ml-counter " + kwCls}>{secondary.length} / {MAX_SECONDARY}</span>
                </span>
                <div className="ml-chipset">
                  {secondary.map((k, i) => <span className="ml-chip" key={i} style={{ height: 28 }}>{k}<span className="ml-chip__x" onClick={() => setSecondary(s => s.filter((_, j) => j !== i))}><MlIcon name="x" size={13} /></span></span>)}
                  {kwDraft === null
                    ? <button className="ml-chip" style={{ height: 28 }} disabled={kwAtLimit}
                              title={kwAtLimit ? `This post already targets ${MAX_SECONDARY} secondary keywords. Remove one to add another.` : "Add a secondary keyword"}
                              onClick={() => setKwDraft("")}><MlIcon name="plus" size={13} />Add</button>
                    : <input className="ml-chip-input" autoFocus value={kwDraft}
                             placeholder="a sub-intent of the focus keyword"
                             onChange={e => setKwDraft(e.target.value)}
                             onBlur={commitKwDraft}
                             onKeyDown={e => {
                               if (e.key === "Enter") { e.preventDefault(); commitKwDraft(); }
                               else if (e.key === "Escape") { e.preventDefault(); setKwDraft(null); }
                             }} />}
                </div>
                <p className={"ml-fieldnote" + (kwAdvice.state === "over" ? " ml-fieldnote--bad" : "")}>{kwAdvice.note}</p>
              </div>

              <div className="ml-seosection">
                <Field label="Slug"><Input value={slug} onChange={e => setSlug(e.target.value)} /></Field>
              </div>

              <div className={"ml-seosection" + flashCls}>
                <span className="ml-seosection__label">Title tag <span className={"ml-counter " + tt.cls}>{tt.text}</span></span>
                <Input value={titleTag} onChange={e => setTitleTag(e.target.value)} placeholder="50-60 characters" />
              </div>

              <div className={"ml-seosection" + flashCls}>
                <span className="ml-seosection__label">Meta description <span className={"ml-counter " + md.cls}>{md.text}</span></span>
                <Textarea rows={3} value={metaDesc} onChange={e => setMetaDesc(e.target.value)} placeholder="150-160 characters" />
                {writeback === "siteatlas" && <p className="ml-fieldnote">Saving publishes the title tag and meta description to the live page head (SiteAtlas). Leave a field blank to use the computed default.</p>}
              </div>

              <div className={"ml-seosection" + flashCls}>
                <Field label="OG title"><Input value={ogTitle} onChange={e => setOgTitle(e.target.value)} placeholder="Open Graph title" /></Field>
              </div>
              <div className={"ml-seosection" + flashCls}>
                <Field label="OG description"><Textarea rows={2} value={ogDesc} onChange={e => setOgDesc(e.target.value)} placeholder="Open Graph description" /></Field>
              </div>

              <div className="ml-seosection">
                <Field label="Canonical URL" hint="Leave blank to use the page's own URL."><Input value={canonical} onChange={e => setCanonical(e.target.value)} placeholder="https://…" leadingIcon="link" /></Field>
              </div>

              <div className="ml-seosection">
                <span className="ml-seosection__label">Search engine visibility</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Switch label="No-index (hide this page from search engines)" checked={noindex} onChange={e => setNoindex(e.target.checked)} />
                  <Switch label="No-follow (don't pass link authority)" checked={nofollow} onChange={e => setNofollow(e.target.checked)} />
                </div>
              </div>

              <div className="ml-seosection">
                <Field label="Schema type">
                  <Select value={schemaType} onChange={e => setSchemaType(e.target.value)}>
                    <option value="">None</option>
                    <option value="Article">Article</option>
                    <option value="BlogPosting">Blog posting</option>
                    <option value="NewsArticle">News article</option>
                    <option value="WebPage">Web page</option>
                    <option value="FAQPage">FAQ page</option>
                    <option value="HowTo">How-to</option>
                    <option value="Product">Product</option>
                  </Select>
                </Field>
                {schemaType && <Button variant="secondary" size="sm" disabled={schemaBusy} leadingIcon={schemaBusy ? <span className="ml-spinner ml-spinner--sm" /> : <MlIcon name="sparkles" size={16} />} onClick={genSchema} style={{ marginTop: 10 }}>{schemaBusy ? "Generating…" : "Generate schema"}</Button>}
              </div>

              <div className="ml-seosection">
                <button className="ml-seo-collapse" onClick={() => setJsonOpen(o => !o)}>
                  <span className="ml-seosection__label" style={{ marginBottom: 0 }}>JSON-LD schema</span>
                  <MlIcon name={jsonOpen ? "chevron-up" : "chevron-down"} size={16} />
                </button>
                {jsonOpen && (
                  schemaJson
                    ? <Textarea rows={8} value={schemaJson} onChange={e => setSchemaJson(e.target.value)} style={{ fontFamily: "monospace", fontSize: 12 }} />
                    : <pre className="ml-jsonld">{`{
  "@context": "https://schema.org",
  "@type": "${schemaType || "Article"}",
  "headline": "${(titleTag || title).slice(0, 60)}",
  "author": { "@type": "Organization", "name": "${(site && site.name) || ""}" },
  "keywords": "${[focusKw, ...secondary].filter(Boolean).join(", ")}",
  "datePublished": "${(post && post.date) ? String(post.date).slice(0, 10) : ""}"
}`}</pre>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Article generator modal (wired to POST /api/claude/generate) ----
  function ArticleGenerator({ open, focusKeyword = "", site, onClose, onGenerate }) {
    const { Button, Field, Select, Input } = DS();
    const [kw, setKw] = useState(focusKeyword);
    const [secondaryInput, setSecondaryInput] = useState("");
    const [style, setStyle] = useState("howto");
    const [length, setLength] = useState("1800");
    const [gen, setGen] = useState(false);
    const [pct, setPct] = useState(0);
    const [phase, setPhase] = useState("");
    const [err, setErr] = useState(null);
    const timer = useRef(null);

    useEffect(() => { setKw(focusKeyword); }, [focusKeyword]);
    useEffect(() => () => { if (typeof timer.current === "function") timer.current(); }, []);
    if (!open) return null;

    const styleMap = { info: "informational", howto: "how-to guide", list: "listicle", compare: "comparison" };

    // What the typed list resolves to under the rule, computed on every render
    // so the field can say "2 of 3" while it is being typed, and can name the
    // ones it will not send before the user spends anything on the generation.
    const genSec = capSecondary(secondaryInput.split(","), kw);

    const start = async () => {
      if (!kw.trim()) { setErr("Enter a focus keyword."); return; }
      setErr(null); setGen(true); setPct(0); setPhase("Starting");
      // Progress comes from the server's own milestones (GET /api/runs/:id)
      // rather than a timer: the bar used to creep to 90% and sit there while a
      // request that had already failed still looked like it was working.
      const runId = newRunId();
      timer.current = pollRun(runId, (run) => { setPct(run.pct); setPhase(run.note ? `${run.label} · ${run.note}` : run.label); });
      try {
        // Capped before it is sent, so the count shown under the field is the
        // count the article is written against. The server caps it again and
        // reports what it trimmed, which is what the notice below reads.
        const secondaryKeywords = capSecondary(secondaryInput.split(","), kw).secondary;
        const r = await api(`/claude/generate`, { method: "POST", body: JSON.stringify({
          run_id: runId,
          focusKeyword: kw.trim(), secondaryKeywords, style: styleMap[style] || "informational",
          length: Number(length) || 1500, site_id: site && (site.site_id != null ? site.site_id : site.id),
        }) });
        timer.current(); setPct(100); setPhase("Done");
        const article = (r && r.article) || null;
        // A draft that hit the length limit is still usable, so it is handed
        // over rather than withheld, but the caller is told so it can say so.
        const notice = (r && r.truncated && r.notice) || null;
        setTimeout(() => { setGen(false); setPct(0); onGenerate && onGenerate(article, notice); }, 250);
      } catch (e) {
        timer.current(); setGen(false); setPct(0); setErr(e.message || "Generation failed");
      }
    };

    const footer = gen
      ? <Button variant="ghost" disabled>Generating…</Button>
      : <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="ai" leadingIcon={<MlIcon name="sparkles" size={18} />} onClick={start}>Generate article</Button></>;

    return React.createElement(DS().Dialog, {
      title: "Generate an article", description: "Marketlens drafts it for you - then hands off to the editor for review.",
      icon: <MlIcon name="sparkles" size={18} />, onClose: gen ? undefined : onClose, footer,
    }, (
      <div style={{ paddingBottom: 16, display: "flex", flexDirection: "column", gap: 18 }}>
        {gen ? (
          <div style={{ padding: "20px 0" }}>
            <div className="ml-progress-row" style={{ marginBottom: 12 }}><div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: pct + "%" }} /></div><span className="ml-progress-row__pct">{pct}%</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}><span className="ml-spinner ml-spinner--sm ml-spinner--ai" />{phase}</div>
          </div>
        ) : (
          <>
            {err && React.createElement(DS().Banner, { tone: "danger", title: "Couldn't generate" }, err)}
            <Field label="Focus keyword"><Input value={kw} onChange={e => setKw(e.target.value)} leadingIcon="search" placeholder="e.g. programmatic seo" /></Field>
            <Field label="Secondary keywords" hint={`Comma-separated. ${genSec.secondary.length} of ${MAX_SECONDARY} used.`}>
              <Input value={secondaryInput} onChange={e => setSecondaryInput(e.target.value)} placeholder="saas seo, scaled content" />
            </Field>
            {genSec.spare.length > 0 && (
              <p className="ml-fieldnote ml-fieldnote--bad" style={{ marginTop: -10 }}>
                An article targets one focus keyword and {MAX_SECONDARY} secondaries, so {genSec.spare.join(", ")} will not be included. Generate {genSec.spare.length === 1 ? "it" : "them"} as a separate piece.
              </p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label="Style"><Select value={style} onChange={e => setStyle(e.target.value)}><option value="info">Informational</option><option value="howto">How-to guide</option><option value="list">Listicle</option><option value="compare">Comparison</option></Select></Field>
              <Field label="Word count"><Select value={length} onChange={e => setLength(e.target.value)}><option value="800">~800 words</option><option value="1200">~1,200 words</option><option value="1800">~1,800 words</option><option value="2500">~2,500 words</option></Select></Field>
            </div>
          </>
        )}
      </div>
    ));
  }

  window.MlEditor = { Editor, ArticleGenerator };
})();
